import locationService from "features/locations/location.service";
import Event from "common/event";
import * as _ from "lodash";
import { ActionWithDispatch } from "common/actionWithDispatch";
import PhysicalLocation from "domain/physicalLocation";
import { trackApiCallNew } from "features/saveIndicator/trackApiCall";
import Commodity from "domain/commodity";
import { Dispatch } from "redux";
import { PositionGroupsLoaded } from "features/positions/positionGroups.action";
import positionGroupService from "features/positions/positionGroupService";
import { TradersLoaded } from "features/settings/traders/traders.actions";
import traderService from "features/settings/traders/traderService";

export class LocationsLoaded extends Event {
  constructor(public locations: PhysicalLocation[]) {
    super(LocationsLoaded.EventName);
  }

  public static EventName: string = "LOCATIONS_LOADED";
}

export class LocationAdded extends Event {
  constructor(public newLocation: PhysicalLocation) {
    super(LocationAdded.EventName);
  }

  public static EventName: string = "LOCATION_ADDED";
}

export class LocationUpdated extends Event {
  constructor(public location: PhysicalLocation) {
    super(LocationUpdated.EventName);
  }

  public static EventName: string = "LOCATION_UPDATED";
}

export class LocationCommoditiesUpdated extends Event {
  constructor(public locationId: string, public commodities: Commodity[]) {
    super(LocationCommoditiesUpdated.EventName);
  }

  public static EventName: string = "LOCATION_COMMODITIES_UPDATED";
}

export class LocationCommodityUpdated extends Event {
  constructor(public locationId: string, public commodity: Commodity) {
    super(LocationCommodityUpdated.EventName);
  }

  public static EventName: string = "LOCATION_COMMODITY_UPDATED";
}

export class CommoditiesUpdatedForAllLocations extends Event {
  constructor(public added: Commodity[], public removed: Commodity[]) {
    super(CommoditiesUpdatedForAllLocations.EventName);
  }

  public static EventName: string = "COMMODITIES_UPDATED_FOR_ALL_LOCATIONS";
}

export class LocationDeleted extends Event {
  constructor(public location: PhysicalLocation) {
    super(LocationDeleted.EventName);
  }

  public static EventName: string = "LOCATION_DELETED";
}

export class DefaultBuyLocationSet extends Event {
  constructor(public location: PhysicalLocation) {
    super(DefaultBuyLocationSet.EventName);
  }

  public static EventName: string = "LOCATION_DEFAULT_BUY_SET";
}

export class InternalOnlyLocationSet extends Event {
  constructor(public location: PhysicalLocation) {
    super(InternalOnlyLocationSet.EventName);
  }

  public static EventName: string = "LOCATION_INTERNAL_ONLY_SET";
}

export function loadLocations(): ActionWithDispatch<LocationsLoaded> {
  return function (dispatch) {
    return locationService
      .listLocations()
      .then(locations => dispatch(new LocationsLoaded(locations).toObject()));
  };
}

export function createLocation_dispatch(dispatch: Dispatch, location: PhysicalLocation, commodities: Commodity[], grantAccess: boolean): Promise<PhysicalLocation> {
  return trackApiCallNew(dispatch, async innerDispatch => {
    const newLocation = await locationService.createLocation(location);
    newLocation.commodities = commodities;

    if (grantAccess) {
      await locationService.grantAccessToAllTraders(newLocation);
      const traderSettings = await traderService.listTraders();
      dispatch(new TradersLoaded(traderSettings).toObject());
    }

    await locationService.updateLocationCommodities(newLocation, commodities);
    innerDispatch(new LocationAdded(newLocation).toObject());
    return newLocation;
  });
}

export function updateLocation(dispatch: Dispatch, location: PhysicalLocation): Promise<PhysicalLocation> {
  return trackApiCallNew(dispatch, async innerDispatch => {
    await locationService.updateLocation(location);
    innerDispatch(new LocationUpdated(location).toObject());

    if (!location.positionGroup?.id && location.positionGroup?.name) {
      const positionGroups = _.orderBy(await positionGroupService.listPositionGroups(), m => m.name);
      innerDispatch(new PositionGroupsLoaded(positionGroups).toObject());
    }

    return location;
  });
}

export function setDefaultBuyLocation(dispatch: Dispatch, location: PhysicalLocation): Promise<any> {
  return trackApiCallNew(dispatch, innerDispatch => {
    return locationService
      .setDefaultBuyLocation(location)
      .then(() => innerDispatch(new DefaultBuyLocationSet(location).toObject()))
  });
}

export function setInternalOnlyLocation(dispatch: Dispatch, location: PhysicalLocation): Promise<any> {
  return trackApiCallNew(dispatch, innerDispatch => {
    return locationService
      .setInternalOnlyLocation(location)
      .then(() => innerDispatch(new InternalOnlyLocationSet(location).toObject()))
  });
}

export function updateLocationCommodities(dispatch: Dispatch, location: PhysicalLocation, commodities: Commodity[]): Promise<any> {
  return trackApiCallNew(dispatch, innerDispatch => {
    innerDispatch(new LocationCommoditiesUpdated(location.id, commodities).toObject());
    return locationService
      .updateLocationCommodities(location, commodities)
      .catch((error) => {
        dispatch(new LocationCommoditiesUpdated(location.id, location.commodities).toObject());
        throw error;
      });
  });
}

export function updateLocationCommodity(dispatch: Dispatch, location: PhysicalLocation, commodity: Commodity): Promise<any> {
  return trackApiCallNew(dispatch, innerDispatch => {
    innerDispatch(new LocationCommodityUpdated(location.id, commodity).toObject());
    return locationService
      .updateLocationCommodity(location, commodity)
      .catch((error) => {
        dispatch(new LocationCommoditiesUpdated(location.id, location.commodities).toObject());
        throw error;
      });
  });
}

export function deleteLocation(dispatch: Dispatch, location: PhysicalLocation): Promise<any> {
  return trackApiCallNew(dispatch, innerDispatch =>
    locationService.deleteLocation(location).then(() => innerDispatch(new LocationDeleted(location).toObject()))
  );
}

export function updateCommoditiesForAllLocations(dispatch: Dispatch, toAdd: Commodity[], toRemove: Commodity[]): Promise<any> {
  return trackApiCallNew(dispatch, innerDispatch => locationService
    .updateCommoditiesForAllLocations(toAdd, toRemove)
    .then(() => innerDispatch(new CommoditiesUpdatedForAllLocations(toAdd, toRemove).toObject()))
  );
}
